import { Box, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import { InsightsResponse, Platform } from "../api/v1/api_pb";
import moment from "moment";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { CIRRUS_RUNNERS_MONTHLY_PRICE, PLATFORMS } from "./constants";
import { grey } from "@mui/material/colors";

interface Props {
  data: InsightsResponse;
  periodDays: number;
}

export default function Comparison(props: Props) {
  const theme = useTheme();
  const { data, periodDays } = props;

  // Calculate GitHub cost
  const platformToSeconds = new Map<Platform, number>();

  for (const aggregate of data.aggregates) {
    for (const metrics of aggregate.platformMetrics) {
      const current = platformToSeconds.get(metrics.platform) ?? 0;

      platformToSeconds.set(
        metrics.platform,
        current + (metrics.computeUsageScaledSeconds?.sum ?? 0),
      );
    }
  }

  const platformToGithubCost = Object.fromEntries(
    Array.from(PLATFORMS.entries()).map(([platform, details]) => {
      const seconds = platformToSeconds.get(platform) ?? 0;
      const minutes = moment.duration(seconds, "seconds").asMinutes();

      // Since Cirrus Runners-provided runners are typically 2x faster on average
      // than GitHub ones[1], assume that the jobs will also run 2x slower
      //
      // [1]: https://runs-on.com/benchmarks/github-actions-cpu-performance/
      const githubSlownessFactor = 2.0;

      return [
        details.label,
        minutes * details.githubMinuteCost * githubSlownessFactor,
      ];
    }),
  );

  const githubCost = Object.values(platformToGithubCost).reduce(
    (accumulator, value) => accumulator + value,
    0,
  );

  // Calculate Cirrus Runners cost
  const runnersCount = Number(data.runnersCount);
  const cirrusRunnersCost =
    runnersCount * CIRRUS_RUNNERS_MONTHLY_PRICE * (periodDays / 30);

  // Do not show comparison when savings are insignificant
  const savings = githubCost - cirrusRunnersCost;
  if (savings <= CIRRUS_RUNNERS_MONTHLY_PRICE * (periodDays / 30)) {
    return null;
  }

  const chartData = [
    {
      name: "GitHub",
      ...platformToGithubCost,
    },
    {
      name: "Cirrus Runners",
      [`${runnersCount} runners`]: cirrusRunnersCost,
    },
  ];

  const priceFormatter = new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
  const compactPriceFormatter = new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    notation: "compact",
    maximumFractionDigits: 1,
  });

  return (
    <Box
      sx={{
        display: "flex",
        gap: 1,
        padding: 2,
        border: 1,
        borderColor: grey[400],
        borderRadius: theme.spacing(0.5),
      }}
    >
      <Box width={theme.spacing(24)} height={theme.spacing(18)}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" interval={0} tick={{ fontSize: 11 }} />
            <YAxis
              ticks={[githubCost]}
              orientation="right"
              tickFormatter={(value) =>
                compactPriceFormatter.format(value).replace("K", "k")
              }
              interval={0}
              domain={[0, (dataMax: number) => dataMax * 1.1]}
              width={Number.parseFloat(theme.spacing(5))}
              tick={{
                fontSize: 10,
                fill: theme.palette.text.secondary,
              }}
            />
            <ReferenceLine
              y={githubCost}
              stroke={grey[900]}
              strokeDasharray="4 4"
            />
            <ChartTooltip
              shared={false}
              cursor={false}
              contentStyle={{
                borderColor: grey[400],
                borderRadius: theme.spacing(0.5),
              }}
              formatter={(value) => priceFormatter.format(Number(value))}
            />
            {Array.from(PLATFORMS.values()).map((platformInfo) => (
              <Bar
                key={platformInfo.label}
                dataKey={platformInfo.label}
                stackId="total"
                fill={platformInfo.githubColor}
              />
            ))}
            <Bar
              dataKey={`${runnersCount} runners`}
              stackId="total"
              fill={theme.palette.primary.main}
            />
          </BarChart>
        </ResponsiveContainer>
      </Box>
      <Stack gap={1}>
        <Typography variant="h5">Win-win!</Typography>
        <Typography color="text.secondary">
          In the past {periodDays} days, you've saved{" "}
          {priceFormatter.format(savings)}
          <Tooltip title="Assumes that equivalent GitHub runners are typically 2x slower">
            <Typography
              component="span"
              color="info"
              sx={{ cursor: "help", fontWeight: "bold" }}
            >
              *
            </Typography>
          </Tooltip>{" "}
          using Cirrus Runners.
        </Typography>
        <Typography color="text.secondary">
          And all that while getting faster runners.
        </Typography>
        <Typography color="text.secondary">
          Great results, let's keep up the pace!
        </Typography>
      </Stack>
    </Box>
  );
}
